import { useCallback, useRef, useState } from "preact/hooks";
import { api, ApiError } from "../api";
import { playAcknowledgement } from "../audio";
import { formatGold } from "../format";
import type { RewardsSummary, WoodOrder } from "../../shared/types";
import {
  WOOD_ORDER_MAX,
  WOOD_ORDER_MIN,
  WOOD_PACK,
  WOOD_PACK_GOLD,
  formatCad,
  formatPackGold,
  formatWoodOrderBounds,
  formatWoodPack,
  woodOrderError,
  woodOrderPrice,
} from "../../shared/wood.ts";
import { Wood } from "./Gold";
import { BusyLabel } from "./BusyLabel";

/** Copies `text`, or, where the clipboard is refused, selects it on screen so a long press can copy it. */
async function copyOrSelect(text: string, el: HTMLElement | null): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    if (el) {
      const range = document.createRange();
      range.selectNodeContents(el);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    return false;
  }
}

/**
 * "100 wood delivered.", once, when the peon's waiting e-transfer request is marked received (not
 * cancelled). A screen hands `observe` the open request from each rewards summary it fetches; when
 * one that was waiting is gone, one GET /api/wood/orders?name= tells received from cancelled.
 */
export function useWoodDelivered(peonName: string): {
  delivered: string | null;
  clearDelivered: () => void;
  observe: (openOrder: WoodOrder | null, enabled?: boolean) => void;
} {
  /** The e-transfer request the last summary showed waiting, to notice the refetch where it lands. */
  const waitingOrder = useRef<string | null>(null);
  const [delivered, setDelivered] = useState<string | null>(null);
  const observe = useCallback(
    (openOrder: WoodOrder | null, enabled = true) => {
      const was = waitingOrder.current;
      waitingOrder.current = openOrder?.id ?? null;
      if (was && !openOrder && enabled) {
        // The request left "open": say so once if it was marked received (not cancelled).
        api.wood.orders(peonName).then(
          ({ mine }) => {
            const settled = mine?.find((o) => o.id === was);
            if (settled?.status === "received") setDelivered(`${formatWoodPack(settled.wood)} delivered.`);
          },
          () => undefined,
        );
      }
    },
    [peonName],
  );
  const clearDelivered = useCallback(() => setDelivered(null), []);
  return { delivered, clearDelivered, observe };
}

/**
 * The Wood card (docs/wood.md), while Dynasty upgrades is on: one pack of wood traded for gold, or
 * as much wood as the peon types bought by e-transfer request, priced live as they type. While the
 * peon's request waits, the card shows where to send the
 * money and the reference code instead of the buttons, until the request is received (by the deposit
 * email or the workshop). Shown on
 * the Rewards screen under its "Wood" heading, and on the Upgrades tab behind "Get wood"; either way
 * it reads the rewards summary (gold, wood, the e-transfer email, the open request).
 */
/** A long address breaks after its "@" before it breaks anywhere else (a wbr costs nothing when it fits). */
function breakAtAt(email: string) {
  const at = email.indexOf("@");
  if (at < 0) return email;
  return (
    <>
      {email.slice(0, at + 1)}
      <wbr />
      {email.slice(at + 1)}
    </>
  );
}

export function WoodCard({
  peonName,
  summary,
  setSummary,
  delivered,
  clearDelivered,
}: {
  peonName: string;
  summary: RewardsSummary;
  setSummary: (update: (s: RewardsSummary | null) => RewardsSummary | null) => void;
  /** "100 wood delivered.", shown once after a request lands, until the next one. */
  delivered: string | null;
  clearDelivered: () => void;
}) {
  const [busy, setBusy] = useState<"trade" | "order" | "cancel" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<"email" | "reference" | null>(null);
  const emailRef = useRef<HTMLSpanElement>(null);
  const referenceRef = useRef<HTMLSpanElement>(null);
  /** What the peon typed in "Wood to buy", as typed; priced and checked by shared/wood.ts. */
  const [woodText, setWoodText] = useState(String(WOOD_PACK));
  const order = summary.open_order;
  const short = WOOD_PACK_GOLD - summary.balance;
  const woodWanted = woodText.trim() === "" ? Number.NaN : Number(woodText);
  const woodWantedError = woodOrderError(woodWanted);
  const priced = Number.isInteger(woodWanted) && woodWanted > 0;

  async function trade() {
    setBusy("trade");
    setError(null);
    try {
      const { balance, wood } = await api.wood.buy(peonName);
      setSummary((s) => (s ? { ...s, balance, wood } : s));
      void playAcknowledgement();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setError("Not enough gold");
      else setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(null);
    }
  }

  async function request() {
    setBusy("order");
    setError(null);
    clearDelivered();
    try {
      const created = await api.wood.order(peonName, woodWanted);
      setSummary((s) => (s ? { ...s, open_order: created } : s));
      void playAcknowledgement();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(null);
    }
  }

  async function cancel(id: string) {
    setBusy("cancel");
    setError(null);
    try {
      await api.wood.cancel(id, peonName);
      setSummary((s) => (s ? { ...s, open_order: null } : s));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not cancel");
    } finally {
      setBusy(null);
    }
  }

  async function copy(which: "email" | "reference", text: string, el: HTMLElement | null) {
    if (await copyOrSelect(text, el)) {
      setCopied(which);
      setTimeout(() => setCopied((c) => (c === which ? null : c)), 2000);
    }
  }

  return (
    <ul class="rewards-list">
      <li class="card reward-card wood-offer">
        <div class="reward-heading">
          <span class="reward-title">
            <Wood amount={order ? order.wood : WOOD_PACK} />
          </span>
          {/* The balance sits where a reward shows its cost. Hidden on the Upgrades tab, which shows
              "Your wood" right above this card (styles.css .upgrades-wood .wood-held). */}
          <span class="muted small wood-held">You have {summary.wood.toLocaleString("en-US")} wood</span>
        </div>
        <p class="reward-blurb">Builds dynasty upgrades.</p>
        {delivered && !order && <p class="muted small rewards-claimed" role="status">{delivered}</p>}
        {order ? (
          <div class="wood-waiting">
            {summary.etransfer_email && (
              <div class="wood-copy-row">
                <p class="muted small wood-copy-label">Send {formatCad(order.amount_cents)} by Interac e-Transfer to</p>
                <span class="wood-email" ref={emailRef}>
                  {breakAtAt(summary.etransfer_email)}
                </span>
                <button type="button" class="btn btn-ghost small-btn" onClick={() => void copy("email", summary.etransfer_email as string, emailRef.current)} aria-label="Copy email">
                  {copied === "email" ? "Copied" : "Copy"}
                </button>
              </div>
            )}
            <div class="wood-copy-row">
              <p class="muted small wood-copy-label">Message, so the wood finds you</p>
              <span class="wood-code" ref={referenceRef}>
                {order.reference}
              </span>
              <button type="button" class="btn btn-ghost small-btn" onClick={() => void copy("reference", order.reference, referenceRef.current)} aria-label="Copy message">
                {copied === "reference" ? "Copied" : "Copy"}
              </button>
            </div>
            <p class="muted small">Wood lands when the e-transfer arrives.</p>
            <button type="button" class="btn btn-ghost small-btn wood-cancel" disabled={busy !== null} onClick={() => void cancel(order.id)}>
              <BusyLabel busy={busy === "cancel"} label="Cancelling…">Cancel request</BusyLabel>
            </button>
          </div>
        ) : (
          <>
            <button type="button" class="btn btn-primary btn-block" disabled={short > 0 || busy !== null} onClick={() => void trade()}>
              <BusyLabel busy={busy === "trade"} label="Trading…">{`Trade ${formatPackGold()}`}</BusyLabel>
            </button>
            {short > 0 && <p class="muted small reward-need">Need {formatGold(short)} more</p>}
            {summary.etransfer_email && (
              <form
                class="wood-buy"
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!woodWantedError && busy === null) void request();
                }}
              >
                {/* The amount and the button that prices it, on one row: the button says the amount back. */}
                <div class="wood-buy-row">
                  <input
                    class="input wood-qty"
                    type="number"
                    inputMode="numeric"
                    aria-label="Wood to buy"
                    min={WOOD_ORDER_MIN}
                    max={WOOD_ORDER_MAX}
                    step={10}
                    value={woodText}
                    onInput={(e) => setWoodText(e.currentTarget.value)}
                  />
                  <button type="submit" class="btn btn-ghost" disabled={!!woodWantedError || busy !== null}>
                    <BusyLabel busy={busy === "order"} label="Asking…">{priced ? `Buy ${formatWoodPack(woodWanted)} for ${formatCad(woodOrderPrice(woodWanted))}` : "Buy wood"}</BusyLabel>
                  </button>
                </div>
                {woodWantedError && <p class="muted small reward-need">{formatWoodOrderBounds()}</p>}
              </form>
            )}
          </>
        )}
        {error && <div class="form-error" role="alert">{error}</div>}
        {/* The Copy buttons' "Copied", said aloud: a live region that stays mounted so the change is announced. */}
        <span class="sr-only" role="status">
          {copied ? "Copied" : ""}
        </span>
      </li>
    </ul>
  );
}
